import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { AlertCircle, CheckCircle2, ShieldAlert, Loader2, Pen, Download } from "lucide-react";
import { toast } from "sonner";
import { SignatureCanvas } from "@/components/SignatureCanvas";

const FN_BASE = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID || ""}.supabase.co/functions/v1`;
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

interface Pack {
  invitation_id: string;
  assignment: any;
  template: any;
  employee: any;
  categories: any[];
  items: any[];
  questions: any[];
  company: any;
  attempts_used: number;
  max_attempts: number;
}

const fmt = (s?: string) => s ? new Date(s).toLocaleDateString("de-DE") : "—";

const SafetyRunPage = () => {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<"loading" | "intro" | "items" | "questions" | "sign" | "result" | "error">("loading");
  const [pack, setPack] = useState<Pack | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<Record<string, { at: number; text: string }>>({});
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [followups, setFollowups] = useState<{ question_text: string; topic: string }[]>([]);
  const [newFollowup, setNewFollowup] = useState("");
  const [signature, setSignature] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${FN_BASE}/safety-token?token=${encodeURIComponent(token)}`, {
          headers: { apikey: ANON, Authorization: `Bearer ${ANON}` },
        });
        const j = await r.json();
        if (!r.ok) { setError(j.error || "fehler"); setPhase("error"); return; }
        setPack(j); setPhase("intro");
      } catch (e: any) { setError(e.message); setPhase("error"); }
    })();
  }, [token]);

  const itemsByCategory = useMemo(() => {
    if (!pack) return [];
    return pack.categories.map(c => ({
      category: c,
      items: pack.items.filter(i => i.category_id === c.id).sort((a, b) => a.sort_order - b.sort_order),
    }));
  }, [pack]);

  const requiredItems = pack?.items.filter(i => i.is_required) || [];
  const allItemsConfirmed = requiredItems.every(i => confirmed[i.id]);
  const requiredQuestions = pack?.questions.filter(q => q.is_required) || [];
  const allQuestionsAnswered = requiredQuestions.every(q => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== "");

  const handleSubmit = async () => {
    if (!pack || !signature) return;
    setSubmitting(true);
    try {
      const itemConfirmations = Object.entries(confirmed).map(([item_id, v]) => ({
        item_id, read_time_seconds: Math.floor(v.at / 1000), confirmation_text: v.text,
      }));
      const answersPayload = Object.entries(answers).map(([question_id, answer_value]) => ({
        question_id, answer_value, time_spent_seconds: undefined,
      }));
      const r = await fetch(`${FN_BASE}/safety-submit`, {
        method: "POST",
        headers: { apikey: ANON, Authorization: `Bearer ${ANON}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          token, itemConfirmations, answers: answersPayload, followups,
          signature_data: signature,
          total_time_seconds: Math.floor((Date.now() - startRef.current) / 1000),
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Fehler");
      setResult(j); setPhase("result");
    } catch (e: any) {
      toast.error(e.message || "Fehler beim Absenden");
    } finally { setSubmitting(false); }
  };

  if (phase === "loading") return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (phase === "error") return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="max-w-md w-full"><CardContent className="pt-6 text-center space-y-3">
        <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
        <h2 className="text-xl font-semibold">Link ungültig oder abgelaufen</h2>
        <p className="text-sm text-muted-foreground">{error === "expired" ? "Dieser Link ist nicht mehr gültig." : error === "completed_or_cancelled" ? "Diese Unterweisung wurde bereits abgeschlossen." : "Bitte fordern Sie eine neue Einladung an."}</p>
      </CardContent></Card>
    </div>
  );
  if (!pack) return null;

  const HeaderBar = () => (
    <div className="bg-[#1B4D5C] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
      <div className="flex items-center gap-3 min-w-0">
        {pack.company?.logo_url ? (
          <img src={pack.company.logo_url} alt={pack.company?.name || "Logo"} className="h-12 w-auto object-contain bg-white rounded p-1 flex-shrink-0" />
        ) : (
          <ShieldAlert className="h-6 w-6 flex-shrink-0" />
        )}
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{pack.template.title}</div>
          <div className="text-xs opacity-80 truncate">{pack.employee.name}</div>
        </div>
      </div>
      <Badge variant="secondary" className="bg-[#E5A835] text-[#1B4D5C] border-0">v{pack.template.version}</Badge>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30">
      <HeaderBar />
      <div className="max-w-3xl mx-auto p-4 pb-24 space-y-4">
        {phase === "intro" && (
          <Card>
            <CardHeader><CardTitle>Willkommen, {pack.employee.name}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p>Sie nehmen an folgender Sicherheitsunterweisung teil:</p>
              <div className="bg-muted p-4 rounded space-y-1">
                <div className="font-semibold text-[#1B4D5C]">{pack.template.title}</div>
                {pack.template.description && <p className="text-sm text-muted-foreground">{pack.template.description}</p>}
                {(pack.template.legal_basis || []).length > 0 && (
                  <p className="text-xs"><strong>Rechtsgrundlage:</strong> {(pack.template.legal_basis as string[]).join(", ")}</p>
                )}
                <p className="text-xs">Bestehensgrenze: {pack.template.passing_score_percent} % · Versuch {pack.attempts_used + 1} von {pack.max_attempts}</p>
              </div>
              <div className="text-sm text-muted-foreground">
                Sie werden die Inhalte abschnittsweise lesen, jeden Punkt bestätigen, ein paar Verständnisfragen beantworten und am Ende digital unterschreiben. Ein erfolgreiches Zertifikat wird automatisch Ihrer Personalakte hinzugefügt.
              </div>
              <Button onClick={() => { startRef.current = Date.now(); setPhase("items"); }} className="w-full bg-[#1B4D5C] hover:bg-[#143b48]">Unterweisung starten</Button>
            </CardContent>
          </Card>
        )}

        {phase === "items" && (
          <div className="space-y-4">
            {itemsByCategory.map(({ category, items }) => (
              <Card key={category.id}>
                <CardHeader><CardTitle className="text-base">{category.title}</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {items.map(it => (
                    <div key={it.id} className="border rounded-lg p-3 space-y-2">
                      <div className="font-medium">{it.title}</div>
                      {it.content && <p className="text-sm whitespace-pre-wrap">{it.content}</p>}
                      {it.risk_description && <div className="text-sm bg-amber-50 border-l-4 border-amber-400 p-2"><strong>Gefahr:</strong> {it.risk_description}</div>}
                      {it.protective_measures && <div className="text-sm bg-emerald-50 border-l-4 border-emerald-400 p-2"><strong>Schutzmaßnahmen:</strong> {it.protective_measures}</div>}
                      {it.image_url && <img src={it.image_url} alt="" className="rounded max-h-64" />}
                      <label className="flex items-start gap-2 cursor-pointer pt-2 border-t">
                        <Checkbox
                          checked={!!confirmed[it.id]}
                          onCheckedChange={(v) => {
                            const next = { ...confirmed };
                            if (v) next[it.id] = { at: Date.now() - startRef.current, text: it.employee_confirmation_text || "Verstanden und zur Kenntnis genommen" };
                            else delete next[it.id];
                            setConfirmed(next);
                          }}
                        />
                        <span className="text-sm">{it.employee_confirmation_text || "Ich habe den Inhalt verstanden und werde die Maßnahmen einhalten."}</span>
                      </label>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
            <Button disabled={!allItemsConfirmed} onClick={() => setPhase("questions")} className="w-full bg-[#1B4D5C] hover:bg-[#143b48]">
              Weiter zu den Verständnisfragen
            </Button>
            {!allItemsConfirmed && <p className="text-xs text-center text-muted-foreground">Bitte alle Pflichtpunkte bestätigen.</p>}
          </div>
        )}

        {phase === "questions" && (
          <div className="space-y-4">
            {pack.questions.length === 0 && <Card><CardContent className="pt-6">Keine Verständnisfragen hinterlegt – weiter zur Unterschrift.</CardContent></Card>}
            {pack.questions.map((q, idx) => (
              <Card key={q.id}>
                <CardContent className="pt-6 space-y-3">
                  <div className="font-medium">{idx + 1}. {q.question}</div>
                  {q.question_type === "yes_no" && (
                    <RadioGroup value={String(answers[q.id] ?? "")} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v === "true" })}>
                      <label className="flex items-center gap-2"><RadioGroupItem value="true" /> Ja</label>
                      <label className="flex items-center gap-2"><RadioGroupItem value="false" /> Nein</label>
                    </RadioGroup>
                  )}
                  {q.question_type === "single_choice" && (
                    <RadioGroup value={String(answers[q.id] ?? "")} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v })}>
                      {(q.answer_options || []).map((opt: any, i: number) => {
                        const label = typeof opt === "string" ? opt : (opt?.label ?? opt?.text ?? String(opt));
                        const value = typeof opt === "string" ? opt : (opt?.id ?? opt?.label ?? label);
                        const rid = `${q.id}-${i}`;
                        return (
                          <label key={i} htmlFor={rid} className="flex items-start gap-2 cursor-pointer py-1">
                            <RadioGroupItem id={rid} value={String(value)} className="mt-0.5" />
                            <span className="text-sm">{label}</span>
                          </label>
                        );
                      })}
                    </RadioGroup>
                  )}
                  {q.question_type === "multi_choice" && (
                    <div className="space-y-1">
                      {(q.answer_options || []).map((opt: any, i: number) => {
                        const label = typeof opt === "string" ? opt : (opt?.label ?? opt?.text ?? String(opt));
                        const value = typeof opt === "string" ? opt : (opt?.id ?? opt?.label ?? label);
                        const cur: string[] = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                        const cid = `${q.id}-${i}`;
                        return (
                          <label key={i} htmlFor={cid} className="flex items-start gap-2 cursor-pointer py-1">
                            <Checkbox id={cid} checked={cur.includes(String(value))} onCheckedChange={(c) => {
                              const next = c ? [...cur, String(value)] : cur.filter(x => x !== String(value));
                              setAnswers({ ...answers, [q.id]: next });
                            }} className="mt-0.5" />
                            <span className="text-sm">{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {q.question_type === "free_text" && (
                    <Textarea value={answers[q.id] || ""} onChange={e => setAnswers({ ...answers, [q.id]: e.target.value })} rows={3} />
                  )}
                  {(q.question_type === "mandatory_confirmation" || q.question_type === "acknowledgement") && (
                    <label className="flex items-center gap-2">
                      <Checkbox checked={!!answers[q.id]} onCheckedChange={(v) => setAnswers({ ...answers, [q.id]: !!v })} />
                      <span>Bestätigen</span>
                    </label>
                  )}
                </CardContent>
              </Card>
            ))}
            <Card>
              <CardHeader><CardTitle className="text-base">Rückfragen (optional)</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <Textarea placeholder="Etwas unklar? Stellen Sie Ihre Frage..." rows={2}
                  value={newFollowup} onChange={e => setNewFollowup(e.target.value)} />
                <Button type="button" variant="outline" size="sm" disabled={!newFollowup.trim()} onClick={() => { setFollowups([...followups, { topic: "Allgemein", question_text: newFollowup.trim() }]); setNewFollowup(""); }}>Frage hinzufügen</Button>
                {followups.map((f, i) => <div key={i} className="text-sm border rounded p-2 bg-muted/40">{f.question_text}</div>)}
              </CardContent>
            </Card>
            <Button disabled={!allQuestionsAnswered} onClick={() => setPhase("sign")} className="w-full bg-[#1B4D5C] hover:bg-[#143b48]">
              Weiter zur Unterschrift
            </Button>
            {!allQuestionsAnswered && <p className="text-xs text-center text-muted-foreground">Bitte alle Pflichtfragen beantworten.</p>}
          </div>
        )}

        {phase === "sign" && (
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Pen className="h-4 w-4" /> Digitale Unterschrift</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm">Ich, <strong>{pack.employee.name}</strong>, bestätige hiermit verbindlich, dass ich die Unterweisung „{pack.template.title}" zur Kenntnis genommen, verstanden und die genannten Pflichten zu beachten habe.</p>
              <div className="border rounded-lg overflow-hidden bg-white">
                <SignatureCanvas onSave={setSignature} />
              </div>
              <Button disabled={!signature || submitting} onClick={handleSubmit} className="w-full bg-[#1B4D5C] hover:bg-[#143b48]">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Verbindlich absenden
              </Button>
            </CardContent>
          </Card>
        )}

        {phase === "result" && result && (
          <Card>
            <CardContent className="pt-8 text-center space-y-4">
              {result.passed ? (
                <>
                  <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" />
                  <h2 className="text-2xl font-semibold">Bestanden!</h2>
                  <p>Score: <strong>{result.score_percent?.toFixed(1)} %</strong></p>
                  <p className="text-sm text-muted-foreground">Das Zertifikat wurde Ihrer Personalakte hinzugefügt und an Ihre E-Mail-Adresse bestätigt.</p>
                </>
              ) : result.needs_review ? (
                <>
                  <AlertCircle className="h-16 w-16 text-amber-500 mx-auto" />
                  <h2 className="text-2xl font-semibold">Eingereicht – Prüfung erforderlich</h2>
                  <p className="text-sm text-muted-foreground">Ihre Freitext-Antworten werden von HR geprüft. Sie erhalten anschließend eine Rückmeldung.</p>
                </>
              ) : (
                <>
                  <AlertCircle className="h-16 w-16 text-destructive mx-auto" />
                  <h2 className="text-2xl font-semibold">Nicht bestanden</h2>
                  <p>Score: <strong>{result.score_percent?.toFixed(1)} %</strong> (erforderlich: {pack.template.passing_score_percent} %)</p>
                  <p className="text-sm text-muted-foreground">Versuch {result.attempt_number} von {result.max_attempts}. Bitte fordern Sie über HR eine neue Einladung an, falls weitere Versuche zulässig sind.</p>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SafetyRunPage;
